import React, {
useEffect,
useState
} from "react";

import {
Avatar,
Button,
Card,
Col,
DatePicker,
Empty,
Form,
Input,
InputNumber,
Modal,
Pagination,
Row,
Space,
Spin,
Table,
Tag,
Upload,
message
} from "antd";

import {
DeleteOutlined,
EditOutlined,
PlusOutlined,
SearchOutlined,
UploadOutlined,
UserOutlined
} from "@ant-design/icons";

import Swal from "sweetalert2";

import dayjs from "dayjs";

import api from "../../api/api";
import { useAcademicYear } from "../../AcademicYearContext";

import "./SupportStaff.css";

const SupportStaff = () => {
const { selectedYear } = useAcademicYear();

const [
    staffs,
    setStaffs
] = useState([]);


const [
    loading,
    setLoading
] = useState(false);


const [
    submitting,
    setSubmitting
] = useState(false);


const [
    openModal,
    setOpenModal
] = useState(false);


const [
    editingId,
    setEditingId
] = useState(null);


const [
    search,
    setSearch
] = useState("");


const [
    page,
    setPage
] = useState(1);


const pageSize = 8;


const [
    form
] = Form.useForm();


const fetchStaffs = async () => {

    setLoading(true);

    try {

        const response = await api.get(
            "/supportstaff/"
        );

        const data = response.data;

        if (Array.isArray(data)) {

            setStaffs(data);

        } else if (data?.results) {

            setStaffs(data.results);

        } else {

            setStaffs([]);

        }

    } catch (error) {

        console.error(
            "Failed to fetch support staff:",
            error
        );

        message.error(
            "Failed to load support staff."
        );

    } finally {

        setLoading(false);

    }

};


useEffect(() => {

    fetchStaffs();

}, []);


const openAddModal = () => {

    form.resetFields();

    setEditingId(null);

    setOpenModal(true);

};


const openEditModal = (staff) => {

    form.setFieldsValue({

        full_name: staff.full_name,

        contact: staff.contact,

        department: staff.department,

        dependent_student:
            staff.dependent_student,

        salary: staff.salary,

        date_add: staff.date_add
            ? dayjs(staff.date_add)
            : null,

    });

    setEditingId(staff.id);

    setOpenModal(true);

};


const closeModal = () => {

    if (!submitting) {

        form.resetFields();

        setEditingId(null);

        setOpenModal(false);

    }

};

const handleSubmit = async (values) => {
    setSubmitting(true);

    try {
        const formData = new FormData();

        formData.append(
            "full_name",
            values.full_name
        );

        formData.append(
            "contact",
            values.contact
        );

        formData.append(
            "department",
            values.department
        );

        formData.append(
            "dependent_student",
            values.dependent_student || ""
        );

        formData.append(
            "salary",
            Number(values.salary).toFixed(2)
        );

        formData.append(
            "date_add",
            values.date_add.format("YYYY-MM-DD")
        );

        // Add image
        if (
            values.image &&
            values.image.fileList &&
            values.image.fileList.length > 0
        ) {
            const file =
                values.image.fileList[0].originFileObj;

            formData.append(
                "image",
                file
            );
        }

        // IMPORTANT:
        // Add academic_year if your backend requires it.
        // Replace this with your selected academic year ID.
        formData.append(
            "academic_year",
            selectedYear.id
        );

        if (editingId) {

            await api.put(
                `/supportstaff/${editingId}/`,
                formData,
                {
                    headers: {
                        "Content-Type":
                            "multipart/form-data"
                    }
                }
            );

            await Swal.fire({
                icon: "success",
                title: "Staff Updated",
                text:
                    "Support staff updated successfully.",
                confirmButtonColor: "#f97316"
            });

        } else {

            await api.post(
                "/supportstaff/",
                formData,
                {
                    headers: {
                        "Content-Type":
                            "multipart/form-data"
                    }
                }
            );

            await Swal.fire({
                icon: "success",
                title: "Staff Added",
                text:
                    "Support staff added successfully.",
                confirmButtonColor: "#f97316"
            });
        }

        closeModal();

        fetchStaffs();

    } catch (error) {

        console.error(
            "Submission failed:",
            error.response?.data ||
            error.message
        );

        message.error(
            error.response?.data?.detail ||
            "Unable to save support staff."
        );

    } finally {

        setSubmitting(false);

    }
};

const handleDelete = (id) => {

    Swal.fire({

        title: "Delete Support Staff?",

        text:
            "This action cannot be undone.",

        icon: "warning",

        showCancelButton: true,

        confirmButtonText:
            "Yes, Delete",

        cancelButtonText:
            "Cancel",

        confirmButtonColor:
            "#dc2626",

        cancelButtonColor:
            "#111827"

    }).then(async (result) => {

        if (!result.isConfirmed) return;


        try {

            await api.delete(
                `/supportstaff/${id}/`
            );


            await Swal.fire({

                icon: "success",

                title: "Deleted",

                text:
                    "Support staff deleted successfully.",

                confirmButtonColor:
                    "#f97316"

            });


            fetchStaffs();


        } catch (error) {

            console.error(
                "Delete failed:",
                error
            );


            message.error(
                "Failed to delete support staff."
            );

        }

    });

};


const getImageUrl = (image) => {

    if (!image) return undefined;


    if (image.startsWith("http")) {

        return image;

    }


    return image;

};


const filteredStaffs = staffs.filter((staff) => {

    const searchValue =
        search.toLowerCase();


    return (

        staff.full_name
            ?.toLowerCase()
            .includes(searchValue)

        ||

        staff.department
            ?.toLowerCase()
            .includes(searchValue)

        ||

        staff.contact
            ?.toLowerCase()
            .includes(searchValue)

        ||

        staff.dependent_student
            ?.toLowerCase()
            .includes(searchValue)

    );

});


const startIndex =
    (page - 1) * pageSize;


const paginatedStaffs =
    filteredStaffs.slice(
        startIndex,
        startIndex + pageSize
    );


const columns = [


    {

        title: "STAFF",

        key: "staff",

        fixed: "left",

        width: 240,

        render: (_, staff) => (

            <Space>

                <Avatar

                    size={48}

                    src={
                        getImageUrl(
                            staff.image
                        )
                    }

                    icon={
                        <UserOutlined />
                    }

                />

                <div>

                    <strong className="staff-name">

                        {staff.full_name}

                    </strong>


                    <div className="staff-id">

                        Staff ID: #{staff.id}

                    </div>

                </div>

            </Space>

        )

    },


    {

        title: "DEPARTMENT",

        dataIndex: "department",

        key: "department",

        width: 180,

        render: (department) => (

            <Tag className="department-tag">

                {department || "Not Assigned"}

            </Tag>

        )

    },


    {

        title: "DEPENDENT STUDENT",

        dataIndex: "dependent_student",

        key: "dependent_student",

        width: 220,

        render: (student) => (

            <span className="table-text">

                {student || "None"}

            </span>

        )

    },


    {

        title: "SALARY",

        dataIndex: "salary",

        key: "salary",

        width: 150,

        render: (salary) => (

            <strong className="salary-text">

                ${Number(salary || 0).toFixed(2)}

            </strong>

        )

    },


    {

        title: "CONTACT",

        dataIndex: "contact",

        key: "contact",

        width: 180,

        render: (contact) => (

            <span className="table-text">

                {contact}

            </span>

        )

    },


    {

        title: "DATE ADDED",

        dataIndex: "date_add",

        key: "date_add",

        width: 150,

        render: (date) => (

            <span className="table-text">

                {date || "—"}

            </span>

        )

    },


    {

        title: "ACTIONS",

        key: "actions",

        fixed: "right",

        width: 130,

        render: (_, staff) => (

            <Space>

                <Button

                    className="edit-button"

                    icon={
                        <EditOutlined />
                    }

                    onClick={() =>
                        openEditModal(staff)
                    }

                />


                <Button

                    danger

                    className="delete-button"

                    icon={
                        <DeleteOutlined />
                    }

                    onClick={() =>
                        handleDelete(staff.id)
                    }

                />

            </Space>

        )

    }

];


return (

    <div className="support-staff-page">


        <div className="support-staff-header">


            <div>

                <div className="page-kicker">

                    STAFF MANAGEMENT

                </div>


                <h1>

                    Support Staff

                </h1>


                <p>

                    Manage your school's support
                    staff records, salaries, departments,
                    and contact information.

                </p>

            </div>


            <Button

                type="primary"

                size="large"

                icon={
                    <PlusOutlined />
                }

                className="add-staff-button"

                onClick={openAddModal}

            >

                Add Support Staff

            </Button>

        </div>


        <Row

            gutter={[16, 16]}

            className="statistics-row"

        >

            <Col xs={24} sm={12} lg={8}>

                <Card className="stat-card orange-card">

                    <div className="stat-label">

                        TOTAL STAFF

                    </div>


                    <div className="stat-value">

                        {staffs.length}

                    </div>

                </Card>

            </Col>


            <Col xs={24} sm={12} lg={8}>

                <Card className="stat-card black-card">

                    <div className="stat-label">

                        DEPARTMENTS

                    </div>


                    <div className="stat-value">

                        {

                            new Set(

                                staffs.map(
                                    staff =>
                                        staff.department
                                )

                            ).size

                        }

                    </div>

                </Card>

            </Col>


            <Col xs={24} sm={24} lg={8}>

                <Card className="stat-card white-card">

                    <div className="stat-label">

                        MONTHLY PAYROLL

                    </div>


                    <div className="stat-value dark-text">

                        $

                        {

                            staffs.reduce(

                                (total, staff) =>

                                    total +
                                    Number(
                                        staff.salary || 0
                                    ),

                                0

                            ).toFixed(2)

                        }

                    </div>

                </Card>

            </Col>

        </Row>


        <Card className="staff-table-card">


            <div className="table-toolbar">


                <div>

                    <h2>

                        Staff Directory

                    </h2>


                    <span>

                        {filteredStaffs.length}
                        {" "}
                        staff records

                    </span>

                </div>


                <Input

                    allowClear

                    prefix={
                        <SearchOutlined />
                    }

                    placeholder="Search staff..."

                    value={search}

                    onChange={(e) => {

                        setSearch(
                            e.target.value
                        );

                        setPage(1);

                    }}

                    className="staff-search"

                />

            </div>


            <Table

                rowKey="id"

                columns={columns}

                dataSource={paginatedStaffs}

                loading={loading}

                pagination={false}

                scroll={{
                    x: 1250
                }}

                locale={{

                    emptyText: (

                        <Empty

                            description=
                                "No support staff found"

                        />

                    )

                }}

            />


            {filteredStaffs.length > pageSize && (

                <div className="pagination-wrapper">

                    <Pagination

                        current={page}

                        pageSize={pageSize}

                        total={
                            filteredStaffs.length
                        }

                        showSizeChanger={false}

                        onChange={(newPage) =>
                            setPage(newPage)
                        }

                    />

                </div>

            )}

        </Card>


        <Modal

            open={openModal}

            onCancel={closeModal}

            footer={null}

            centered

            destroyOnClose

            width={620}

            className="staff-modal"

            title={

                <div className="modal-title">

                    <div className="modal-icon">

                        <UserOutlined />

                    </div>


                    <div>

                        <h2>

                            {editingId
                                ? "Edit Support Staff"
                                : "Add Support Staff"}

                        </h2>


                        <p>

                            Enter the staff member's
                            information below.

                        </p>

                    </div>

                </div>

            }

        >


            <Form

                form={form}

                layout="vertical"

                onFinish={handleSubmit}

                className="staff-form"

            >


                <Row gutter={16}>


                    <Col xs={24} md={12}>

                        <Form.Item

                            label="Full Name"

                            name="full_name"

                            rules={[

                                {

                                    required: true,

                                    message:
                                        "Please enter full name."

                                }

                            ]}

                        >

                            <Input

                                size="large"

                                placeholder=
                                    "Enter full name"

                            />

                        </Form.Item>

                    </Col>


                    <Col xs={24} md={12}>

                        <Form.Item

                            label="Contact"

                            name="contact"

                            rules={[

                                {

                                    required: true,

                                    message:
                                        "Please enter contact."

                                }

                            ]}

                        >

                            <Input

                                size="large"

                                placeholder=
                                    "Phone or contact"

                            />

                        </Form.Item>

                    </Col>


                    <Col xs={24} md={12}>

                        <Form.Item

                            label="Department"

                            name="department"

                            rules={[

                                {

                                    required: true,

                                    message:
                                        "Please enter department."

                                }

                            ]}

                        >

                            <Input

                                size="large"

                                placeholder=
                                    "e.g. Security"

                            />

                        </Form.Item>

                    </Col>


                    <Col xs={24} md={12}>

                        <Form.Item

                            label="Dependent Student"

                            name="dependent_student"

                        >

                            <Input

                                size="large"

                                placeholder=
                                    "Enter dependent student"

                            />

                        </Form.Item>

                    </Col>


                    <Col xs={24} md={12}>

                        <Form.Item

                            label="Salary"

                            name="salary"

                            rules={[

                                {

                                    required: true,

                                    message:
                                        "Please enter salary."

                                }

                            ]}

                        >

                            <InputNumber

                                size="large"

                                min={0}

                                step={0.01}

                                precision={2}

                                prefix="$"

                                style={{
                                    width: "100%"
                                }}

                                placeholder="0.00"

                            />

                        </Form.Item>

                    </Col>


                    <Col xs={24} md={12}>

                        <Form.Item

                            label="Date Added"

                            name="date_add"

                            rules={[

                                {

                                    required: true,

                                    message:
                                        "Please select date."

                                }

                            ]}

                        >

                            <DatePicker

                                size="large"

                                format="YYYY-MM-DD"

                                style={{
                                    width: "100%"
                                }}

                            />

                        </Form.Item>

                    </Col>


                    <Col xs={24}>

                        <Form.Item

                            label="Profile Image"

                            name="image"

                            valuePropName="file"

                            getValueFromEvent={

                                (event) => event

                            }

                        >

                            <Upload

                                beforeUpload={() =>
                                    false
                                }

                                maxCount={1}

                                accept=
                                    "image/png,image/jpeg"

                            >

                                <Button

                                    icon={
                                        <UploadOutlined />
                                    }

                                >

                                    Select Image

                                </Button>

                            </Upload>

                        </Form.Item>

                    </Col>

                </Row>


                <div className="modal-actions">


                    <Button

                        size="large"

                        onClick={closeModal}

                        disabled={submitting}

                    >

                        Cancel

                    </Button>


                    <Button

                        type="primary"

                        htmlType="submit"

                        size="large"

                        loading={submitting}

                        className="save-button"

                    >

                        {editingId
                            ? "Update Staff"
                            : "Save Staff"}

                    </Button>

                </div>

            </Form>

        </Modal>

    </div>

);


};

export default SupportStaff;





